import { ConfirmDialog } from 'components/confirm-dialog/confirm-dialog'
// Schedule report drawer — Figma "Data Sheet" (node 6458:26852).
//
// Right-side slide-in drawer opened by the canvas header's "Schedule" button.
// Single-purpose: a small form for report scope, what it covers, cadence, starting
// date, date range, an optional white-label logo upload, and a "white label report"
// toggle, with a primary "Schedule" action pinned at the bottom. Report history used
// to be a "Past reports" tab here but was hard to find (Cherry #0006), so it now links
// out to the dedicated Scheduled Reports page instead.
//
// The overlay/slide-in shell, Esc-to-close and third-party-widget z-index
// suppression mirror the chart customization drawer so both feel identical.
import {
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode
} from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { usePrefersReducedMotion } from './customize-reveal'
import {
	AppButton,
	CanvasSpaceTitle,
	Checkbox,
	DotMatrix,
	Input,
	ListItem,
	MenuList,
	MenuListDivider,
	Radio,
	RadioGroup,
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	Toggle,
	Tooltip,
	Typography,
	colors
} from '@clickguard/design-system'
import {
	ArrowUpRight,
	Calendar,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Download01,
	HelpCircle,
	Image01,
	Mail01,
	Plus,
	Trash01,
	Upload01,
	XClose
} from './icons'
// Brand marks for the ad-account / campaign pickers — imported as URLs and
// rendered as <img> (never inline SVGR): the Vite demos build drops the CRA
// ReactComponent export, and shared ids across brand SVGs cross-contaminate fills
// when inlined. Same assets + rationale as source-icon.tsx (the dimension table).
import googleLogo from 'assets/img/google.svg'
import metaLogo from 'assets/img/meta.svg'
import microsoftLogo from 'assets/img/microsoft.svg'
import { getScrollParent } from './scroll-parent'
import { acquirePositioned, releasePositioned } from 'components/data-table-v2'
import { useAccountStore, useAssetStore } from 'store'
import { ScheduledReports, AnalyticsReport, intervalToPeriod } from 'services/user'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import {
	useReportLayoutStore,
	DEFAULT_BRAND_COLOR,
	type ScheduleCfg,
	type WhiteLabelLogoSource
} from '../store/use-report-layout-store'
import { useWebsiteBranding } from '../data/use-website-branding'
import { useWebsiteFavicon } from 'hooks/use-website-favicon'
import { useViewSchedule } from '../data/use-view-schedule'
import { DownloadOverlay } from './pdf/download-overlay'
import './schedule-report-drawer.css'

const PLATFORM_LOGO: Record<PlatformKey, string> = {
	google: googleLogo,
	microsoft: microsoftLogo,
	meta: metaLogo
}
const PlatformLogo = ({
	platform,
	className = 'h-4 w-4'
}: {
	platform: PlatformKey
	className?: string
}) => (
	<img
		src={PLATFORM_LOGO[platform]}
		alt=""
		aria-hidden
		className={`shrink-0 object-contain ${className}`}
	/>
)

// ── Field options ────────────────────────────────────────────────────────────
// Report scope is fixed to the website the report belongs to (read-only). Coverage
// is narrowed instead via the two "Display data from" dropdowns below; the first
// option of each ("All …") is the default and includes everything.
const WEBSITE = 'clickguard.com'
// Ad accounts + campaigns are multi-selectable and each carries its ad platform,
// so the picker can show the platform's brand logo (mirrors the dimension table /
// the top-nav breadcrumb ad-account picker) — users scan by logo, not just name.
// `platform` keys the same brand marks the dimension table uses (source-icon).
type PlatformKey = 'google' | 'microsoft' | 'meta'
type SelectOption = { id: string; name: string; platform: PlatformKey }

// The scope pickers list the website's REAL ad accounts + campaigns, sourced from
// the asset store (the same data the breadcrumb uses). Campaign platform is
// resolved via its ad account so each option shows the right logo.
const PLATFORM_KEY: Record<'GADS' | 'FADS' | 'BADS', PlatformKey> = {
	GADS: 'google',
	FADS: 'meta',
	BADS: 'microsoft'
}
const useScopeOptions = (): {
	adAccounts: SelectOption[]
	campaigns: SelectOption[]
} => {
	const storeAdAccounts = useAssetStore((s) => s.adAccounts)
	const storeCampaigns = useAssetStore((s) => s.campaigns)
	return useMemo(() => {
		const platformById = new Map<string, PlatformKey>()
		const adAccounts = storeAdAccounts.map((a) => {
			const platform = PLATFORM_KEY[a.platform] ?? 'google'
			platformById.set(a.id, platform)
			return { id: a.id, name: a.name, platform }
		})
		const campaigns = storeCampaigns.map((c) => ({
			id: c.id,
			name: c.name,
			platform: platformById.get(c.ad_account_id) ?? 'google'
		}))
		return { adAccounts, campaigns }
	}, [storeAdAccounts, storeCampaigns])
}
const INTERVAL = ['Daily', 'Weekly', 'Monthly', 'Quarterly']
const RANGE = [
	'Last 7 days',
	'Last 30 days',
	'This month',
	'Last month',
	'This quarter',
	'Last quarter'
]

// Map the drawer's display labels to the backend enums (DEV-2377, §6.1).
const INTERVAL_TO_ENUM: Record<string, 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY'> = {
	Daily: 'DAILY',
	Weekly: 'WEEKLY',
	Monthly: 'MONTHLY',
	Quarterly: 'QUARTERLY'
}
const RANGE_TO_ENUM: Record<
	string,
	'LAST_7' | 'LAST_30' | 'THIS_MONTH' | 'LAST_MONTH' | 'THIS_QUARTER' | 'LAST_QUARTER'
> = {
	'Last 7 days': 'LAST_7',
	'Last 30 days': 'LAST_30',
	'This month': 'THIS_MONTH',
	'Last month': 'LAST_MONTH',
	'This quarter': 'THIS_QUARTER',
	'Last quarter': 'LAST_QUARTER'
}

// ── Brand colour swatches ─────────────────────────────────────────────────────
// Sourced straight from the DS palette so the white-label picker only ever offers
// on-brand colours (no arbitrary hexes / design drift). Spread across hues for a
// 4×4 grid. `colors` is loosely typed here, so narrow each ramp to a string map.
const C = colors as unknown as {
	primary: Record<string, string>
	navy: Record<string, string>
	green: Record<string, string>
	red: Record<string, string>
	amber: Record<string, string>
	lemon: Record<string, string>
	chart: Record<string, string>
}
const BRAND_SWATCHES = [
	C.primary['500'],
	C.primary['400'],
	C.navy['500'],
	C.navy['400'],
	C.green['500'],
	C.green['600'],
	C.green['700'],
	C.lemon['100'],
	C.red['500'],
	C.red['600'],
	C.amber['500'],
	C.amber['600'],
	C.chart['neon'],
	C.chart['violet'],
	C.chart['rose'],
	C.chart['pink']
]

// A white-label profile can't be saved without a logo, so the Save is gated until
// one is uploaded. Shown on the disabled Save button.
const WHITE_LABEL_SAVE_TIP =
	'Upload a logo to save white label branding.'

// Readable text colour over a swatch — white on dark fills, dark-navy on light
// ones — via perceived luminance, so the preview header text never washes out.
const readableOn = (hex: string): string => {
	const h = hex.replace('#', '')
	const n =
		h.length === 3
			? h
					.split('')
					.map((c) => c + c)
					.join('')
			: h
	const r = parseInt(n.slice(0, 2), 16)
	const g = parseInt(n.slice(2, 4), 16)
	const b = parseInt(n.slice(4, 6), 16)
	return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#171053' : '#ffffff'
}

// ── Scroll-anchored popover ───────────────────────────────────────────────────
// Every popover in this drawer (select / calendar / colour grid) anchors the same
// way: portal it as an ABSOLUTE child of the nearest scroll container, positioned
// in that container's content coordinates. The browser then scrolls the popover
// natively, in lockstep with the trigger — there is NO scroll listener and NO
// JS repositioning. A `position: fixed` popover chased by an onScroll handler
// always trails the compositor by a frame and visibly bounces/"dances" as you
// scroll; an absolute child of the scroller is moved by the same paint as the
// content, so it stays glued. The container's own overflow clips it, and the
// portal escapes any inner overflow-hidden. Mirrors components/icon-menu.tsx.
type AnchorPos = { top: number; left: number; width?: number }

// `place(triggerRect, containerRect, container)` returns the popover's position in
// the container's scrollable content space.
const useScrollAnchoredPopover = (
	place: (t: DOMRect, sr: DOMRect, sc: HTMLElement) => AnchorPos
) => {
	const [open, setOpen] = useState(false)
	const [pos, setPos] = useState<AnchorPos | null>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const scRef = useRef<HTMLElement | null>(null)
	// Container mutation goes through the shared ref-counted lease, exactly like
	// icon-menu / anchored-popover. See positioned-lease.ts for why per-instance
	// restore of a SHARED container is unsafe.
	const heldRef = useRef(false)
	// Mirror `open` in a ref so onExitComplete reads the latest value.
	const openRef = useRef(false)
	openRef.current = open

	const acquireLease = (sc: HTMLElement) => {
		if (heldRef.current) return
		acquirePositioned(sc)
		heldRef.current = true
	}
	const releaseLease = () => {
		if (!heldRef.current) return
		const sc = scRef.current
		if (sc) releasePositioned(sc)
		heldRef.current = false
	}

	// `place` is redefined inline by each caller every render, so hold it in a ref
	// and keep `reposition` identity-stable — otherwise the layout effect below
	// would re-fire on every render.
	const placeRef = useRef(place)
	placeRef.current = place
	const reposition = useCallback(() => {
		const t = triggerRef.current
		const sc = scRef.current
		if (!t || !sc) return
		setPos(placeRef.current(t.getBoundingClientRect(), sc.getBoundingClientRect(), sc))
	}, [])

	const openPopover = () => {
		// getScrollParent only reports a container that is CURRENTLY overflowing,
		// so on a short tab (White label) it walks past the drawer body and returns
		// the document scroller — which sits outside the drawer's own fixed
		// z-[9999] layer, so the popover paints UNDER the drawer and swallows every
		// click. The drawer body is the correct containing block whether or not it
		// happens to scroll; fall back to it before the page.
		const t = triggerRef.current
		const sc =
			getScrollParent(t, null) ??
			t?.closest<HTMLElement>('.sched-scroll') ??
			(document.scrollingElement as HTMLElement)
		scRef.current = sc
		acquireLease(sc)
		// NOTE: do NOT reposition() here — the layout effect below measures after
		// the open render but BEFORE paint, so the first painted frame is already
		// at the anchor instead of one frame at the previous open's position.
		setOpen(true)
	}

	// Measure the moment `open` flips true, before the browser paints.
	useLayoutEffect(() => {
		if (open) reposition()
	}, [open, reposition])

	// Release the lease if we unmount while still open (AnimatePresence goes with
	// us, so onExitComplete never fires and `relative` would leak).
	useEffect(() => () => releaseLease(), [])

	// Hand back to each caller's <AnimatePresence>: the container must NOT be
	// restored until the exit animation has finished.
	const onExitComplete = () => {
		if (!openRef.current) releaseLease()
	}

	useEffect(() => {
		if (!open) return
		const onDown = (e: MouseEvent) => {
			const n = e.target as Node
			if (triggerRef.current?.contains(n) || menuRef.current?.contains(n))
				return
			setOpen(false)
		}
		const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
		// No scroll listener — native scrolling keeps the popover glued. Recompute
		// only on resize (the trigger may move on layout reflow).
		const onResize = () => reposition()
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onEsc)
		window.addEventListener('resize', onResize)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onEsc)
			window.removeEventListener('resize', onResize)
		}
		// eslint-disable-next-line -- bind listeners only on open toggle
	}, [open])

	return {
		open,
		setOpen,
		pos,
		triggerRef,
		menuRef,
		scRef,
		openPopover,
		reposition,
		onExitComplete
	}
}

// ── Portaled select (label on top) ───────────────────────────────────────────
// Same visual treatment as the chart drawer's SelectField, but lighter: a short
// list anchored in the scroll container (see useScrollAnchoredPopover) so it
// escapes the drawer's overflow clip and rides the scroll without jitter.
const LabeledSelect = ({
	label,
	info,
	value,
	options,
	onChange
}: {
	label?: string
	/** Optional helper copy — when set, an info glyph sits beside the label and
	 *  surfaces this text on hover (same HelpCircle + portaled Tooltip as the
	 *  white-label checkbox below). */
	info?: string
	value: string
	options: string[]
	onChange: (next: string) => void
}) => {
	const reduced = usePrefersReducedMotion()
	// Anchored in the scroll container; the dropdown matches the field width.
	const {
		open,
		setOpen,
		pos,
		triggerRef,
		menuRef,
		scRef,
		openPopover,
		onExitComplete
	} = useScrollAnchoredPopover((t, sr, sc) => ({
			top: t.bottom - sr.top + sc.scrollTop + 4,
			left: t.left - sr.left + sc.scrollLeft,
			width: t.width
		}))

	return (
		<div className="flex w-full flex-col gap-1.5">
			{label && (
				<span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
					{label}
					{info && (
						<Tooltip
							content={info}
							position="top"
							portal
							className="pa-drawer-tip"
						>
							<span
								className="flex h-3.5 w-3.5 cursor-help items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-3.5 [&>svg]:w-3.5"
								aria-label={info}
							>
								<HelpCircle />
							</span>
						</Tooltip>
					)}
				</span>
			)}
			<button
				ref={triggerRef}
				type="button"
				aria-haspopup="listbox"
				aria-expanded={open}
				onClick={() => (open ? setOpen(false) : openPopover())}
				className="flex min-h-[36px] w-full items-center justify-between gap-2 rounded-lg border border-[rgb(var(--cg-border))] bg-white px-3 py-2 text-left shadow-[0px_1px_1px_rgba(16,24,40,0.05)] transition-colors hover:bg-[rgb(var(--cg-surface-subtle))]"
			>
				<span className="truncate text-base text-gray-900">{value}</span>
				<span
					className={`flex h-4 w-4 shrink-0 items-center justify-center text-[rgb(var(--cg-text-tertiary))] transition-transform [&>svg]:h-4 [&>svg]:w-4 ${
						open ? 'rotate-180' : ''
					}`}
				>
					<ChevronDown />
				</span>
			</button>
			{scRef.current &&
				createPortal(
					<AnimatePresence onExitComplete={onExitComplete}>
						{open && pos && (
							<motion.div
								ref={menuRef}
								role="listbox"
								className="absolute z-50"
								style={
									{
										left: pos.left,
										top: pos.top,
										width: pos.width,
										transformOrigin: 'top left'
									} as React.CSSProperties
								}
								initial={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }
								}
								animate={
									reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }
								}
								exit={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.985 }
								}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								<MenuList>
									{options.map((opt) => (
										<ListItem
											key={opt}
											label={opt}
											selected={opt === value}
											onClick={() => {
												onChange(opt)
												setOpen(false)
											}}
										/>
									))}
								</MenuList>
							</motion.div>
						)}
					</AnimatePresence>,
					scRef.current
				)}
		</div>
	)
}

const FieldLabel = ({ children }: { children: ReactNode }) => (
	<span className="text-sm font-medium text-gray-900">{children}</span>
)

// ── Multi-select with brand logos (ad accounts / campaigns) ───────────────────
// Same portaled, scroll-anchored popover shell as LabeledSelect, but the rows are
// checkboxes (multi-select) each led by the platform's brand logo, plus an "All …"
// master row — mirroring the top-nav breadcrumb ad-account picker. The trigger
// summarises the selection: stacked unique logos + "All …" when everything is on,
// a single logo + name when one is picked, stacked logos + "N …" for a subset.
const uniquePlatforms = (opts: SelectOption[]): PlatformKey[] => {
	const seen = new Set<PlatformKey>()
	const out: PlatformKey[] = []
	for (const o of opts)
		if (!seen.has(o.platform)) {
			seen.add(o.platform)
			out.push(o.platform)
		}
	return out
}

// Overlapping logo cluster, each on a white ring so they read as distinct coins.
const LogoStack = ({ platforms }: { platforms: PlatformKey[] }) => (
	<span className="flex shrink-0 items-center -space-x-1">
		{platforms.map((p) => (
			<span
				key={p}
				className="flex h-4 w-4 items-center justify-center rounded-full bg-white ring-2 ring-white"
			>
				<PlatformLogo platform={p} className="h-3.5 w-3.5" />
			</span>
		))}
	</span>
)

const LabeledMultiSelect = ({
	label,
	info,
	options,
	selected,
	onChange,
	allLabel,
	summaryNoun,
	headerNoun
}: {
	label?: string
	info?: string
	options: SelectOption[]
	/** Selected option ids. */
	selected: string[]
	onChange: (next: string[]) => void
	/** Trigger label when every option is selected, e.g. "All ad accounts". */
	allLabel: string
	/** Plural noun for the "N …" summary, the search placeholder and the empty
	 *  line, e.g. "ad accounts" → "Search for ad accounts…". */
	summaryNoun: string
	/** Sentence-case plural for the panel's count caption, e.g. "Ad accounts". */
	headerNoun: string
}) => {
	const reduced = usePrefersReducedMotion()
	const panelId = useId()
	const {
		open,
		setOpen,
		pos,
		triggerRef,
		menuRef,
		scRef,
		openPopover,
		onExitComplete
	} = useScrollAnchoredPopover((t, sr, sc) => ({
			top: t.bottom - sr.top + sc.scrollTop + 4,
			left: t.left - sr.left + sc.scrollLeft,
			width: t.width
		}))

	const allOn = selected.length === options.length && options.length > 0
	const selectedOpts = options.filter((o) => selected.includes(o.id))

	// Search — mirrors the top-nav breadcrumb pickers (components/breadcrumb-bar/
	// campaigns-selector.tsx): case-insensitive substring on the name. Cleared on
	// each open so a stale query never hides rows the next time you come back.
	const [query, setQuery] = useState('')
	useEffect(() => {
		if (!open) setQuery('')
	}, [open])
	const visible = useMemo(() => {
		const q = query.trim().toLowerCase()
		return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options
	}, [options, query])

	// Select all acts on what you can SEE, like the breadcrumb picker: with a
	// filter applied it selects/clears just the matches, leaving the rest of the
	// selection alone. Checked/indeterminate track the visible set for the same
	// reason — otherwise the box contradicts the list under it.
	const allVisibleOn =
		visible.length > 0 && visible.every((o) => selected.includes(o.id))
	const someVisibleOn =
		!allVisibleOn && visible.some((o) => selected.includes(o.id))

	const toggle = (id: string) =>
		onChange(
			selected.includes(id)
				? selected.filter((x) => x !== id)
				: [...selected, id]
		)
	const toggleAll = () => {
		const ids = visible.map((o) => o.id)
		onChange(
			allVisibleOn
				? selected.filter((id) => !ids.includes(id))
				: Array.from(new Set([...selected, ...ids]))
		)
	}

	// Trigger summary.
	const trigger =
		allOn || selected.length === 0 ? (
			<span className="flex min-w-0 items-center gap-2">
				<LogoStack platforms={uniquePlatforms(options)} />
				<span
					className={`truncate text-base ${
						selected.length === 0 ? 'text-[#6b7280]' : 'text-gray-900'
					}`}
				>
					{selected.length === 0 ? `Select ${summaryNoun}` : allLabel}
				</span>
			</span>
		) : selectedOpts.length === 1 ? (
			<span className="flex min-w-0 items-center gap-2">
				<PlatformLogo platform={selectedOpts[0].platform} />
				<span className="truncate text-base text-gray-900">
					{selectedOpts[0].name}
				</span>
			</span>
		) : (
			<span className="flex min-w-0 items-center gap-2">
				<LogoStack platforms={uniquePlatforms(selectedOpts)} />
				<span className="truncate text-base text-gray-900">
					{selected.length} {summaryNoun}
				</span>
			</span>
		)

	return (
		<div className="flex w-full flex-col gap-1.5">
			{label && (
				<span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
					{label}
					{info && (
						<Tooltip
							content={info}
							position="top"
							portal
							className="pa-drawer-tip"
						>
							<span
								className="flex h-3.5 w-3.5 cursor-help items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-3.5 [&>svg]:w-3.5"
								aria-label={info}
							>
								<HelpCircle />
							</span>
						</Tooltip>
					)}
				</span>
			)}
			<button
				ref={triggerRef}
				type="button"
				// Disclosure, not a popup: aria-haspopup would promise a
				// menu/listbox/tree/grid/dialog role we do not have.
				aria-controls={open ? panelId : undefined}
				aria-expanded={open}
				onClick={() => (open ? setOpen(false) : openPopover())}
				className="flex min-h-[36px] w-full items-center justify-between gap-2 rounded-lg border border-[rgb(var(--cg-border))] bg-white px-3 py-2 text-left shadow-[0px_1px_1px_rgba(16,24,40,0.05)] transition-colors hover:bg-[rgb(var(--cg-surface-subtle))]"
			>
				{trigger}
				<span
					className={`flex h-4 w-4 shrink-0 items-center justify-center text-[rgb(var(--cg-text-tertiary))] transition-transform [&>svg]:h-4 [&>svg]:w-4 ${
						open ? 'rotate-180' : ''
					}`}
				>
					<ChevronDown />
				</span>
			</button>
			{scRef.current &&
				createPortal(
					<AnimatePresence onExitComplete={onExitComplete}>
						{open && pos && (
							<motion.div
								ref={menuRef}
								// A named group of checkboxes. NOT role="listbox": that promises
								// arrow/Home/End navigation and a roving tab stop we do not
								// implement, and each row is a real checkbox with native keys.
								id={panelId}
								role="group"
								aria-label={headerNoun}
								className="absolute z-50"
								style={
									{
										left: pos.left,
										top: pos.top,
										width: pos.width,
										transformOrigin: 'top left'
									} as React.CSSProperties
								}
								initial={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }
								}
								animate={
									reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }
								}
								exit={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.985 }
								}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								{/* Panel shape copied from the top-nav breadcrumb picker
								    (breadcrumb-bar/campaigns-selector.tsx): count caption,
								    search, Select all, divider, scrollable rows. */}
								<MenuList className="flex max-h-[360px] flex-col">
									<div className="px-3 pb-2 pt-1">
										<Typography variant="caption" color="muted">
											{headerNoun} · {selected.length} of {options.length}{' '}
											selected
										</Typography>
									</div>
									<div className="px-2 pb-1">
										<Input
											size="sm"
											state="default"
											placeholder={`Search for ${summaryNoun}...`}
											value={query}
											onChange={(e) => setQuery(e.target.value)}
										/>
									</div>
									{/* Select all — checked when every VISIBLE option is on,
									    indeterminate when only some are. */}
									<MultiRow
										checked={allVisibleOn}
										indeterminate={someVisibleOn}
										onToggle={toggleAll}
										label="Select all"
									/>
									<MenuListDivider />
									<div className="min-h-0 flex-1 overflow-y-auto">
										{visible.length > 0 ? (
											visible.map((o) => (
												<MultiRow
													key={o.id}
													checked={selected.includes(o.id)}
													onToggle={() => toggle(o.id)}
													leading={<PlatformLogo platform={o.platform} />}
													label={o.name}
												/>
											))
										) : (
											<div className="px-3 py-2">
												<Typography variant="body-sm" color="muted">
													No {summaryNoun} found
												</Typography>
											</div>
										)}
									</div>
								</MenuList>
							</motion.div>
						)}
					</AnimatePresence>,
					scRef.current
				)}
		</div>
	)
}

// One selectable row: checkbox + leading (logo/stack) + name. The whole row is the
// click target; the DS Checkbox is display-only (readOnly) so it never double-fires.
const MultiRow = ({
	checked,
	indeterminate,
	onToggle,
	leading,
	label
}: {
	checked: boolean
	indeterminate?: boolean
	onToggle: () => void
	/** Leading logo/stack. Omitted on the Select-all row, which has no platform. */
	leading?: ReactNode
	label: string
}) => (
	// DS ListItem with the platform mark in `leadingIcon` and the checkbox in
	// `leadingControl`. Do NOT pass `selected` — it auto-renders a trailing purple
	// check that competes with the checkbox already saying the same thing.
	//
	// This panel is a search field + a list of checkboxes, so it says exactly that:
	// the wrapper is a `group` and the CHECKBOX is the real control. It is NOT a
	// listbox — role="listbox"/"option" promises arrow-key navigation, Home/End and
	// a single roving tab stop, none of which we implement; claiming the role
	// without the keyboard model is worse than not claiming it.
	//
	// `as="div"` is deliberate (the breadcrumb picker we mirrored uses the default
	// <button> and is wrong for it): ListItem defaults to <button>, and DS Checkbox
	// renders a real <input type="checkbox"> — interactive content, which may never
	// sit inside a button. A div has no content restrictions, so the live checkbox
	// is legal here and keeps its native keyboard behaviour (Tab to reach, Space to
	// toggle) with no hand-rolled key handling.
	//
	// The row stays clickable as a convenience; the checkbox stops propagation so a
	// click on the box itself doesn't also bubble to the row and toggle twice.
	<ListItem
		as="div"
		label={label}
		onClick={onToggle}
		leadingControl={
			<span
				className="flex items-center"
				onClick={(e: React.MouseEvent) => e.stopPropagation()}
			>
				<Checkbox
					size="sm"
					checked={checked}
					indeterminate={indeterminate}
					onChange={onToggle}
					aria-label={label}
				/>
			</span>
		}
		leadingIcon={leading}
	/>
)

// ── Fading logo image ─────────────────────────────────────────────────────────
// A freshly-picked logo otherwise pops in the instant the blob decodes. Start it
// transparent and fade to full opacity on `load` (~200ms) so it settles softly.
// Keyed on `src` upstream so re-uploading a logo re-runs the fade.
const LogoImg = ({
	src,
	className = ''
}: {
	src: string
	className?: string
}) => {
	const [loaded, setLoaded] = useState(false)
	return (
		<img
			src={src}
			alt=""
			onLoad={() => setLoaded(true)}
			className={`${className} transition-opacity duration-200 ease-out ${
				loaded ? 'opacity-100' : 'opacity-0'
			}`}
		/>
	)
}

// ── Date helpers ──────────────────────────────────────────────────────────────
// The field's value is a plain `YYYY-MM-DD` string (kept so the form state shape
// doesn't change). Parse/format LOCALLY — `new Date('2026-06-24')` parses as UTC
// midnight, which renders as the day before in western timezones, so build the
// Date from parts instead.
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December'
]
const parseISO = (s: string): Date => {
	const [y, m, d] = s.split('-').map(Number)
	return new Date(y, (m || 1) - 1, d || 1)
}
const toISO = (d: Date): string =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
		d.getDate()
	).padStart(2, '0')}`
// Spell the month out in full ("May 26, 2026") rather than a numeric MM/DD/YYYY:
// the audience spans the US (month-first) and the rest of the world (day-first), so
// a numeric date is genuinely ambiguous. A named month can't be misread either way.
const formatDisplay = (d: Date): string =>
	`${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
const sameDay = (a: Date, b: Date): boolean =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate()

// When the report next generates: step from the start date by the cadence until
// we pass today. For an already-running schedule that's the next upcoming run;
// for a start date still in the future it's the start date itself.
const INTERVAL_STEP: Record<string, (d: Date) => void> = {
	Daily: (d) => d.setDate(d.getDate() + 1),
	Weekly: (d) => d.setDate(d.getDate() + 7),
	Monthly: (d) => d.setMonth(d.getMonth() + 1),
	Quarterly: (d) => d.setMonth(d.getMonth() + 3)
}
const nextGeneration = (interval: string, startISO: string): Date => {
	const step = INTERVAL_STEP[interval] ?? INTERVAL_STEP.Weekly
	const d = parseISO(startISO)
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	let guard = 0
	while (d <= today && guard++ < 1000) step(d)
	return d
}

// ── Date picker field (calendar popover) ──────────────────────────────────────
// Replaces the native `<input type="date">`: a field-box trigger showing the
// formatted date, plus a month-grid calendar anchored in the scroll container
// (see useScrollAnchoredPopover), right-aligned to the field's ending edge.
const MENU_MIN_W = 268 // floor so the 7-column grid stays readable on a narrow trigger

const DatePickerField = ({
	label,
	value,
	onChange
}: {
	label: string
	value: string
	onChange: (next: string) => void
}) => {
	const reduced = usePrefersReducedMotion()
	const selected = parseISO(value)
	const today = new Date()
	// The month the grid is showing (independent of the selection so you can page
	// around without changing the value); reset to the selected month on each open.
	const [view, setView] = useState(
		() => new Date(selected.getFullYear(), selected.getMonth(), 1)
	)

	// Right-aligned to the field's ending edge: the field is only ~half the row and
	// sits on the drawer's right side, so the wider calendar lines its right edge up
	// with the field's. Clamp to the container's left padding so it never spills off.
	const {
		open,
		setOpen,
		pos,
		triggerRef,
		menuRef,
		scRef,
		openPopover,
		onExitComplete
	} = useScrollAnchoredPopover((t, sr, sc) => {
			const width = Math.max(MENU_MIN_W, t.width)
			const right = t.right - sr.left + sc.scrollLeft
			return {
				top: t.bottom - sr.top + sc.scrollTop + 4,
				left: Math.max(8, right - width),
				width
			}
		})

	// Reset to the selected month, then open.
	const openCalendar = () => {
		setView(new Date(selected.getFullYear(), selected.getMonth(), 1))
		openPopover()
	}
	// If the calendar opened partly past the container's bottom edge, bring it fully
	// into view by scrolling the container natively (no flip, no fixed-position jump).
	useEffect(() => {
		if (!open) return
		const id = requestAnimationFrame(() =>
			menuRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
		)
		return () => cancelAnimationFrame(id)
		// eslint-disable-next-line -- reveal once per open
	}, [open])

	// Leading blanks to align day 1 under its weekday, then each day of the month.
	const firstWeekday = new Date(view.getFullYear(), view.getMonth(), 1).getDay()
	const daysInMonth = new Date(
		view.getFullYear(),
		view.getMonth() + 1,
		0
	).getDate()
	const cells: (number | null)[] = [
		...Array(firstWeekday).fill(null),
		...Array.from({ length: daysInMonth }, (_, i) => i + 1)
	]
	const shiftMonth = (delta: number) =>
		setView((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1))

	return (
		<div className="flex w-full flex-col gap-1.5">
			<FieldLabel>{label}</FieldLabel>
			<button
				ref={triggerRef}
				type="button"
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={() => (open ? setOpen(false) : openCalendar())}
				className={`flex min-h-[36px] w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-left shadow-[0px_1px_1px_rgba(16,24,40,0.05)] transition-colors hover:bg-[rgb(var(--cg-surface-subtle))] ${
					open ? 'border-[rgb(var(--cg-brand))]' : 'border-[rgb(var(--cg-border))]'
				}`}
			>
				<span className="truncate text-base text-gray-900">
					{formatDisplay(selected)}
				</span>
				<span className="flex h-4 w-4 shrink-0 items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-4 [&>svg]:w-4">
					<Calendar />
				</span>
			</button>
			{scRef.current &&
				createPortal(
					<AnimatePresence onExitComplete={onExitComplete}>
						{open && pos && (
							<motion.div
								ref={menuRef}
								role="dialog"
								aria-label="Choose starting date"
								className="absolute z-50 rounded-xl border border-[rgb(var(--cg-border))] bg-white p-3 shadow-[0px_8px_24px_-4px_rgba(16,24,40,0.18)]"
								style={
									{
										left: pos.left,
										top: pos.top,
										width: pos.width,
										transformOrigin: 'top right'
									} as React.CSSProperties
								}
								initial={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }
								}
								animate={
									reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }
								}
								exit={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.985 }
								}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								{/* Month header + prev/next nav. */}
								<div className="mb-2 flex items-center justify-between">
									<button
										type="button"
										aria-label="Previous month"
										onClick={() => shiftMonth(-1)}
										className="flex h-7 w-7 items-center justify-center rounded-lg text-[rgb(var(--cg-text-tertiary))] transition-colors hover:bg-[#f4f3ff] hover:text-gray-700 [&>svg]:h-4 [&>svg]:w-4"
									>
										<ChevronLeft />
									</button>
									<span className="text-sm font-medium text-gray-900">
										{MONTHS[view.getMonth()]} {view.getFullYear()}
									</span>
									<button
										type="button"
										aria-label="Next month"
										onClick={() => shiftMonth(1)}
										className="flex h-7 w-7 items-center justify-center rounded-lg text-[rgb(var(--cg-text-tertiary))] transition-colors hover:bg-[#f4f3ff] hover:text-gray-700 [&>svg]:h-4 [&>svg]:w-4"
									>
										<ChevronRight />
									</button>
								</div>
								{/* Weekday header row. */}
								<div className="grid grid-cols-7 justify-items-center gap-0.5">
									{WEEKDAYS.map((w) => (
										<span
											key={w}
											className="flex h-8 items-center justify-center text-xs font-medium text-[#6b7280]"
										>
											{w}
										</span>
									))}
								</div>
								{/* Day grid. */}
								<div className="grid grid-cols-7 justify-items-center gap-0.5">
									{cells.map((day, i) => {
										if (day === null) return <span key={`b${i}`} />
										const d = new Date(view.getFullYear(), view.getMonth(), day)
										const isSel = sameDay(d, selected)
										const isToday = sameDay(d, today)
										return (
											<button
												key={day}
												type="button"
												aria-label={formatDisplay(d)}
												aria-pressed={isSel}
												onClick={() => {
													onChange(toISO(d))
													setOpen(false)
												}}
												className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm transition-colors ${
													isSel
														? 'bg-[rgb(var(--cg-brand))] font-medium text-white'
														: isToday
														? 'font-medium text-[rgb(var(--cg-brand))] hover:bg-[#f4f3ff]'
														: 'text-gray-900 hover:bg-[#f4f3ff]'
												}`}
											>
												{day}
											</button>
										)
									})}
								</div>
							</motion.div>
						)}
					</AnimatePresence>,
					scRef.current
				)}
		</div>
	)
}

// ── Brand colour picker (swatch grid) ─────────────────────────────────────────
// A small swatch trigger that opens a portaled grid of brand-palette colours —
// same anchor-on-scroll-follow pattern as LabeledSelect so it escapes the drawer
// clip. The chosen colour tints the white-label report preview below.
const ColorPickerField = ({
	label,
	value,
	onChange
}: {
	label: string
	value: string
	onChange: (next: string) => void
}) => {
	const reduced = usePrefersReducedMotion()
	const [draft, setDraft] = useState(value.replace('#', ''))
	const inputRef = useRef<HTMLInputElement>(null)
	// Anchored in the scroll container, left-aligned under the swatch trigger.
	const {
		open,
		setOpen,
		pos,
		triggerRef,
		menuRef,
		scRef,
		openPopover,
		onExitComplete
	} = useScrollAnchoredPopover((t, sr, sc) => ({
			top: t.bottom - sr.top + sc.scrollTop + 6,
			left: t.left - sr.left + sc.scrollLeft
		}))

	// Mirror the hex field when the colour changes from the grid — but never while
	// the field has focus. A half-typed value is not a value to reformat, and
	// rewriting the draft under the cursor fills the field to maxLength and eats
	// every remaining keystroke.
	useEffect(() => {
		if (document.activeElement === inputRef.current) return
		if (value.toLowerCase() !== `#${draft.toLowerCase()}`)
			setDraft(value.replace('#', ''))
		// eslint-disable-next-line -- sync from value only
	}, [value])

	// Accept a typed hex: keep only hex digits, and commit only a full 6-digit
	// value while typing. A 3-digit shorthand is ALSO the first half of a 6-digit
	// one, so committing it mid-word paints a colour the user never asked for
	// (typing "ff0000" hits "ff0" on the way and lands on #ffff00, yellow).
	// Shorthand resolves on blur instead, once the value is final.
	const expand = (h: string) =>
		(h.length === 3
			? h
					.split('')
					.map((c) => c + c)
					.join('')
			: h
		).toLowerCase()
	const onHex = (raw: string) => {
		const h = raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 6)
		setDraft(h)
		if (h.length === 6) onChange(`#${expand(h)}`)
	}
	// On blur: expand a 3-digit shorthand, else revert to the live colour if the
	// value never became complete.
	const onHexBlur = () => {
		if (draft.length === 3) onChange(`#${expand(draft)}`)
		else if (draft.length !== 6) setDraft(value.replace('#', ''))
	}

	return (
		<div className="flex flex-col gap-1.5">
			<FieldLabel>{label}</FieldLabel>
			<div className="flex items-center gap-2">
				<button
					ref={triggerRef}
					type="button"
					aria-haspopup="dialog"
					aria-expanded={open}
					aria-label={`Brand color: ${value}`}
					onClick={() => (open ? setOpen(false) : openPopover())}
					className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[rgb(var(--cg-border))] bg-white p-1 shadow-[0px_1px_1px_rgba(16,24,40,0.05)] transition-colors hover:bg-[rgb(var(--cg-surface-subtle))]"
				>
					<span
						className="h-full w-full rounded-[5px]"
						style={{ backgroundColor: value }}
					/>
				</button>
				{/* Type your own hex — commits on a valid 3/6-digit value. Field styling is
				    identical to the Email recipient input above (rounded-lg / #e1e1e1 border /
				    1px shadow / px-3 / 36px / focus:#554bfd) so the form reads as one set of
				    fields; just compact and #-prefixed. Kept narrow so the swatch + field
				    sit beside the logo box without pushing the row past the drawer padding. */}
				<div className="flex min-h-[36px] items-center gap-1 rounded-lg border border-[rgb(var(--cg-border))] bg-white px-3 shadow-[0px_1px_1px_rgba(16,24,40,0.05)] focus-within:border-[rgb(var(--cg-brand))]">
					<span className="text-base text-[#6b7280]">#</span>
					<input
						ref={inputRef}
						value={draft}
						onChange={(e) => onHex(e.target.value)}
						onBlur={onHexBlur}
						spellCheck={false}
						maxLength={6}
						aria-label="Brand color hex"
						className="w-[60px] bg-transparent text-base uppercase text-gray-900 outline-none"
					/>
				</div>
			</div>
			{scRef.current &&
				createPortal(
					<AnimatePresence onExitComplete={onExitComplete}>
						{open && pos && (
							<motion.div
								ref={menuRef}
								role="dialog"
								aria-label="Choose brand color"
								className="absolute z-50 grid grid-cols-4 gap-2 rounded-xl border border-[rgb(var(--cg-border))] bg-white p-2.5 shadow-[0px_8px_24px_-4px_rgba(16,24,40,0.18)]"
								style={
									{
										left: pos.left,
										top: pos.top,
										transformOrigin: 'top left'
									} as React.CSSProperties
								}
								initial={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }
								}
								animate={
									reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }
								}
								exit={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.985 }
								}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								{BRAND_SWATCHES.map((c) => {
									const sel = c.toLowerCase() === value.toLowerCase()
									return (
										<button
											key={c}
											type="button"
											aria-label={c}
											aria-pressed={sel}
											onClick={() => {
												onChange(c)
												setOpen(false)
											}}
											className={`h-9 w-9 rounded-lg transition-transform hover:scale-105 ${
												sel ? 'ring-2 ring-[#554bfd] ring-offset-2' : ''
											}`}
											style={{ backgroundColor: c }}
										/>
									)
								})}
							</motion.div>
						)}
					</AnimatePresence>,
					scRef.current
				)}
		</div>
	)
}

// ── Editable report name ──────────────────────────────────────────────────────
// Inline rename via the DS CanvasSpaceTitle. Its hover fill is pure CSS
// (`group-hover:cg-bg-hover`), and browsers DON'T re-evaluate :hover when content
// moves under a stationary cursor — so scrolling the drawer body (or the
// <input>→<button> swap on commit) parks a grey fill that won't clear until you
// physically move the mouse again. We force the fill flat (`sched-title-flat`,
// `background-color: transparent !important`) whenever that staleness can occur —
// on commit AND on any scroll — then release it on the next real pointer move,
// when ordinary :hover takes back over.
const EditableTitle = ({
	value,
	onValueChange
}: {
	value: string
	onValueChange: (next: string) => void
}) => {
	const [flat, setFlat] = useState(false)
	// A scroll moves the title relative to the cursor without a pointer event, so
	// the browser keeps a stale :hover — flatten until the pointer actually moves.
	useEffect(() => {
		const onScroll = () => setFlat(true)
		document.addEventListener('scroll', onScroll, true)
		return () => document.removeEventListener('scroll', onScroll, true)
	}, [])
	return (
		<CanvasSpaceTitle
			value={value}
			onValueChange={(next) => {
				onValueChange(next)
				setFlat(true)
			}}
			editable
			size="md"
			// `self-start` keeps the inline-flex title at its content width — without it
			// the flex-col card stretches it edge-to-edge, so the group-hover fill would
			// light up across the whole row instead of just under the text.
			className={`self-start${flat ? ' sched-title-flat' : ''}`}
			onPointerMove={() => flat && setFlat(false)}
		/>
	)
}

// ── Schedule tab body ────────────────────────────────────────────────────────
// Which Share-drawer tab is active - schedule cadence vs white-label branding.
export type ShareTab = 'schedule' | 'whitelabel'

// Closes the loop back to the Scheduled Reports list: after scheduling here the
// report shows up there (the Scheduled tab of the asset's /reports page), so the
// confirmation toast offers a jump to it.
const goToScheduledReports = (accountId?: string, assetId?: string) => {
	if (!accountId || !assetId) return
	window.location.assign(`/workspace/${accountId}/asset/${assetId}/reports`)
}

const ScheduledToast = ({
	children,
	accountId,
	assetId
}: {
	children: ReactNode
	accountId?: string
	assetId?: string
}) => (
	<div className="flex flex-col items-start gap-1.5">
		<span>{children}</span>
		<button
			type="button"
			onClick={() => goToScheduledReports(accountId, assetId)}
			className="text-sm font-semibold underline underline-offset-2"
		>
			View scheduled reports
		</button>
	</div>
)

// Best human-readable message for a failed schedule save. The API returns
// { message, data: [{ param, msg }] } for validation errors, whose default msg
// ("Invalid value") is useless on its own — name the offending field so the user
// (and we) can see exactly what the backend rejected.
const scheduleErrorMessage = (err: unknown, fallback: string): string => {
	const data = (
		err as {
			response?: {
				data?: { message?: string; data?: Array<{ param?: string; msg?: string }> }
			}
		}
	)?.response?.data
	const field = data?.data?.[0]
	if (field?.param) return `${field.msg ?? 'Invalid value'} (${field.param})`
	return data?.message || fallback
}

// The three white-label logo sources, rendered as bare DS `Radio` rows.
// `emptyTip` is the tooltip shown when a source has no image yet — that source's
// row is then disabled. Custom has none (you always upload one), so it's never
// disabled.
const LOGO_SOURCES: {
	value: WhiteLabelLogoSource
	label: string
	emptyTip?: string
}[] = [
	{
		value: 'workspace',
		label: 'Workspace logo',
		emptyTip: 'No logo on this workspace yet.'
	},
	{
		value: 'website',
		label: 'Website logo',
		emptyTip: 'No logo on this website yet.'
	},
	{ value: 'custom', label: 'Custom logo' }
]

// The image + replace card that reveals directly below the selected source. It
// shows the resolved logo for that source (or a placeholder when there's none)
// and the button that opens the file picker to replace it.
const LogoCard = ({
	image,
	onReplace
}: {
	image: string | null
	onReplace: () => void
}) => (
	<div className="flex items-center gap-3 rounded-xl border border-[rgb(var(--cg-border))] p-3">
		<span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[rgb(var(--cg-border))] bg-white p-1.5">
			{image ? (
				<img src={image} alt="" className="h-full w-full object-contain" />
			) : (
				<Image01 className="h-5 w-5 text-[rgb(var(--cg-text-tertiary))]" />
			)}
		</span>
		<AppButton
			variant="secondary"
			size="sm"
			label={image ? 'Replace image' : 'Upload image'}
			leftIcon={<Upload01 className="h-5 w-5" />}
			onClick={onReplace}
		/>
	</div>
)

const ShareForm = ({
	name,
	setName,
	onSchedule,
	tab,
	whiteLabel,
	logoSource,
	setLogoSource,
	effectiveLogo,
	logoImages,
	websiteUsesFavicon,
	brandColor,
	adAccounts,
	campaigns,
	adAccountOptions,
	campaignOptions,
	onToggleWhiteLabel,
	onPickLogo,
	setBrandColor,
	setAdAccounts,
	setCampaigns,
	brandingNeedsLogo,
	canSaveWhiteLabel,
	onSaveWhiteLabel,
	pastReportsLink,
	schedule,
	onScheduleChanged
}: {
	name: string
	setName: (n: string) => void
	onSchedule: () => void
	tab: ShareTab
	whiteLabel: boolean
	/** Which image feeds the header logo. */
	logoSource: WhiteLabelLogoSource
	setLogoSource: (s: WhiteLabelLogoSource) => void
	/** The resolved logo for the selected source (workspace/website avatar or custom). */
	effectiveLogo: string | null
	websiteUsesFavicon: boolean
	/** Per-source image; a workspace/website source with none is disabled. */
	logoImages: Record<WhiteLabelLogoSource, string | null>
	brandColor: string
	adAccounts: string[]
	campaigns: string[]
	adAccountOptions: SelectOption[]
	campaignOptions: SelectOption[]
	onToggleWhiteLabel: (e: React.ChangeEvent<HTMLInputElement>) => void
	onPickLogo: (e: React.ChangeEvent<HTMLInputElement>) => void
	setBrandColor: (c: string) => void
	setAdAccounts: (ids: string[]) => void
	setCampaigns: (ids: string[]) => void
	/** White-label logo missing while enabled — blocks Save, shows the field hint. */
	brandingNeedsLogo: boolean
	/** Working branding differs from the committed profile AND is valid. */
	canSaveWhiteLabel: boolean
	/** Persist the working branding on this website. */
	onSaveWhiteLabel: () => void
	/** "View scheduled reports" for the Schedule footer; null when the reports page
	 *  is unreachable (auth-free preview). Built by the drawer, which owns routing. */
	pastReportsLink: ReactNode
	/** The schedule on record for the active view (from the backend), or null
	 *  when the view has never been scheduled. Its presence is what makes this
	 *  drawer read Stop/Save instead of Schedule. */
	schedule: ScheduleCfg | null
	/** Re-read the schedule list after a create / update / stop landed. */
	onScheduleChanged: () => Promise<unknown>
}) => {
	const reduced = usePrefersReducedMotion()
	// The report itself decides what this drawer is: a schedule on record means
	// Stop/Save, none means Schedule. That record comes from the BACKEND (any
	// teammate's schedule for this workspace + website + view) — not from how the
	// drawer was opened and not from a local cache — so Manage (deep link), Share
	// (header button), another browser and another user all read the same thing.
	const activeViewId = useReportLayoutStore((s) => s.activeViewId)
	const scheduled = !!schedule
	// Backend persistence context for creating the scheduled report.
	const { accountId: schAccountId, assetId: schAssetId } = useParams()

	// Seed the form from the schedule on record so "current scheduling" is the
	// real thing, not defaults dressed up as one. `name` is lifted to the drawer
	// (so the Download button can title the PDF) and arrives as a prop.
	const [interval, setInterval] = useState(schedule?.interval ?? 'Weekly')
	// Default the starting date to today: a fixed seed drifts into the past and a
	// schedule that starts "last week" reads as broken.
	const [startDate, setStartDate] = useState(
		() => schedule?.startDate ?? toISO(new Date())
	)
	const [range, setRange] = useState(schedule?.range ?? 'Last 30 days')
	// Email recipients — the scheduled report is sent to each address. Multiple
	// allowed (ported from the TQA lead-magnet invite panel, simplified to a plain
	// send-to list: no owner/role/verification, just who gets the report).
	const [recipients, setRecipients] = useState<string[]>(
		schedule?.recipients ?? []
	)
	const [recipientDraft, setRecipientDraft] = useState('')
	const [recipientError, setRecipientError] = useState<string | null>(null)
	const addRecipient = () => {
		const e = recipientDraft.trim().toLowerCase()
		if (!e) return
		if (!/^\S+@\S+\.\S+$/.test(e)) {
			setRecipientError('Enter a valid email.')
			return
		}
		if (recipients.some((x) => x.toLowerCase() === e)) {
			setRecipientError('Already added.')
			return
		}
		setRecipients((list) => [...list, e])
		setRecipientDraft('')
		setRecipientError(null)
	}
	const removeRecipient = (email: string) =>
		setRecipients((list) => list.filter((x) => x !== email))

	// Change tracking for a scheduled report: `savedKey` is seeded from the values
	// on record and re-seeded on save, so "Save changes" only ENABLES once the form
	// actually differs from what's scheduled. The footer summary reads the stored
	// schedule directly, so it can never claim a cadence nobody set.
	const currentKey = JSON.stringify({
		name,
		adAccounts,
		campaigns,
		interval,
		startDate,
		range,
		recipients,
		recipientDraft: recipientDraft.trim().toLowerCase()
	})
	const [savedKey, setSavedKey] = useState<string | null>(null)
	useEffect(() => {
		setSavedKey(currentKey)
		// mount-only snapshot of the incoming schedule
		// eslint-disable-next-line
	}, [])
	const dirty = scheduled && savedKey !== null && currentKey !== savedKey

	// Stop scheduling — confirmed through the DS ConfirmDialog (tone="danger"),
	// which brings its own destructive confirm button. The drawer stays open: the
	// report is simply unscheduled now, so the footer flips back to "Schedule".
	const [confirmingStop, setConfirmingStop] = useState(false)
	const handleStopSchedule = async () => {
		const reportId = schedule?.reportId
		try {
			// Delete the persisted row first, then re-read the list; a failed delete
			// must not read as "stopped" while the backend keeps emailing it.
			if (reportId && schAccountId && schAssetId) {
				await ScheduledReports.stop(schAccountId, schAssetId, reportId)
			}
			await onScheduleChanged()
			setConfirmingStop(false)
			toast.success('Report scheduling stopped.')
		} catch {
			toast.error('Could not stop the schedule. Please try again.')
		}
	}

	// Scheduling flushes a half-typed recipient the user never pressed Add/Enter on,
	// so it isn't silently dropped: commit it if valid, or block + surface the error
	// so they fix or clear it before the report is scheduled.
	const handleSchedule = async () => {
		const e = recipientDraft.trim().toLowerCase()
		let committed = recipients
		if (e) {
			if (!/^\S+@\S+\.\S+$/.test(e)) {
				setRecipientError('Enter a valid email or clear it before scheduling.')
				return
			}
			if (!recipients.some((x) => x.toLowerCase() === e)) {
				committed = [...recipients, e]
				setRecipients(committed)
			}
			setRecipientDraft('')
			setRecipientError(null)
		}
		// Scheduled reports are view-based (generation reads the view's LIVE config),
		// so a saved view is required — guide the user to save one first if they're on
		// the ad-hoc default report.
		if (!activeViewId) {
			toast.error('Save this report as a view before scheduling it.')
			return
		}
		// A scheduled report is emailed to its recipients, so at least one is
		// required — surface it inline instead of letting the backend reject the
		// save with a generic "Invalid value".
		if (!committed.length) {
			setRecipientError('Add at least one recipient email before scheduling.')
			return
		}
		// Fields common to create + edit; create also binds the view.
		const payload = {
			label: name || 'Analytics report',
			ad_account_ids: adAccounts,
			campaign_ids: campaigns,
			period: intervalToPeriod(INTERVAL_TO_ENUM[interval] ?? 'WEEKLY'),
			analytics_date_range: RANGE_TO_ENUM[range] ?? 'LAST_30',
			starting_date: startDate,
			recipients: committed
		}
		const createPayload = { ...payload, view_id: activeViewId }
		const canPersist = !!(schAccountId && schAssetId)

		if (scheduled) {
			// Editing a live schedule: save in place, keep the drawer open, refresh
			// the summary and re-arm the CTA. No close, no "starts …" toast. Persist
			// first — only reflect the change locally once the backend confirms it.
			const reportId = schedule?.reportId
			if (!canPersist) {
				toast.error('Could not save the schedule changes. Please try again.')
				return
			}
			try {
				if (reportId) {
					await ScheduledReports.update(schAccountId, schAssetId, reportId, payload)
				} else {
					await ScheduledReports.create(schAccountId, schAssetId, createPayload)
				}
				await onScheduleChanged()
			} catch (err) {
				toast.error(
					scheduleErrorMessage(err, 'Could not save the schedule changes. Please try again.')
				)
				return
			}
			setSavedKey(
				JSON.stringify({
					name,
					adAccounts,
					campaigns,
					interval,
					startDate,
					range,
					recipients: committed,
					recipientDraft: ''
				})
			)
			toast.success(
			<ScheduledToast accountId={schAccountId} assetId={schAssetId}>
				Schedule updated.
			</ScheduledToast>
		)
			return
		}
		// Fresh schedule — persist to the backend FIRST, then record it locally and
		// confirm. A failed (or un-sendable) save must not masquerade as scheduled.
		if (!canPersist) {
			toast.error('Could not save the schedule. Please try again.')
			return
		}
		try {
			await ScheduledReports.create(schAccountId, schAssetId, createPayload)
			await onScheduleChanged()
		} catch (err) {
			toast.error(
				scheduleErrorMessage(err, 'Could not save the schedule. Please try again.')
			)
			return
		}
		// The drawer closes on schedule, so this toast is the only confirmation the
		// action actually landed (the app-wide ToastContainer renders it).
		toast.success(
			<ScheduledToast accountId={schAccountId} assetId={schAssetId}>
				Report scheduled. Starts {formatDisplay(parseISO(startDate))}.
			</ScheduledToast>
		)
		onSchedule()
	}
	const fileRef = useRef<HTMLInputElement>(null)

	// Drawer-body overlay scrollbar: transparent at rest, fades in only while the
	// body is actively scrolled (`is-scrolling`), then fades out ~700ms after the
	// last scroll — instead of the always-on global purple bar that reserves a
	// gutter and shoves the content left.
	const [scrolling, setScrolling] = useState(false)
	const scrollTimer = useRef<ReturnType<typeof setTimeout>>()
	const onBodyScroll = () => {
		setScrolling(true)
		clearTimeout(scrollTimer.current)
		scrollTimer.current = setTimeout(() => setScrolling(false), 700)
	}
	useEffect(() => () => clearTimeout(scrollTimer.current), [])

	return (
		<>
			<div
				onScroll={onBodyScroll}
				className={`sched-scroll flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-6 pt-4${
					scrolling ? ' is-scrolling' : ''
				}`}
			>
				{/* Schedule <-> White label swap - same directional x-slide as the customize
				    drawer's Metrics/Charts tabs; the gap-6 stack moves onto the motion.div.
				    Both tabs share this form's state so Schedule can gate on the logo. */}
				<AnimatePresence mode="wait" initial={false}>
					<motion.div
						key={tab}
						className="flex flex-col gap-6"
						initial={
							reduced
								? false
								: { opacity: 0, x: tab === 'whitelabel' ? 10 : -10 }
						}
						animate={reduced ? { opacity: 1 } : { opacity: 1, x: 0 }}
						exit={
							reduced
								? { opacity: 0 }
								: { opacity: 0, x: tab === 'whitelabel' ? -10 : 10 }
						}
						transition={
							reduced
								? { duration: 0 }
								: { duration: 0.2, ease: [0.16, 1, 0.3, 1] }
						}
					>
						{tab === 'schedule' ? (
							<>
								{/* Report name card — inline-editable, same rename affordance as the
				    customize drawer's section name. */}
								<div className="flex flex-col gap-0.5 rounded-xl border border-[rgb(var(--cg-border))] p-4">
									<span className="flex items-center gap-1.5 px-2 text-sm text-[#6b7280]">
										Report name
										<Tooltip
											content="This is the title recipients see on the report you send them. It's separate from the report's name inside ClickGuard."
											position="top"
											portal
											className="pa-drawer-tip"
										>
											<span
												className="flex h-3.5 w-3.5 cursor-help items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-3.5 [&>svg]:w-3.5"
												aria-label="This is the title recipients see on the report you send them. It's separate from the report's name inside ClickGuard."
											>
												<HelpCircle />
											</span>
										</Tooltip>
									</span>
									<EditableTitle value={name} onValueChange={setName} />
								</div>

								{/* Report scope — fixed to the website this report belongs to. Read-only
				    (no chevron, muted fill): the website can't be changed here. */}
								<div className="flex flex-col gap-1.5">
									<span className="text-sm font-medium text-gray-900">
										Report scope
									</span>
									<div className="flex min-h-[36px] w-full items-center rounded-lg border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface-subtle))] px-3 py-2">
										<span className="truncate text-base text-gray-900">
											{WEBSITE}
										</span>
									</div>
								</div>

								{/* Display data from — narrow coverage to specific ad accounts / campaigns.
				    Both default to "All …" (everything); the section title labels the pair,
				    so the dropdowns carry no redundant per-field label. */}
								<div className="flex flex-col gap-1.5">
									<span className="text-sm font-medium text-gray-900">
										Display data from
									</span>
									{/* Stacked (not side-by-side) so each picker spans the full
				    drawer width — campaign names can be long and need room to
				    show in full. */}
									<div className="flex flex-col gap-2">
										<LabeledMultiSelect
											options={adAccountOptions}
											selected={adAccounts}
											onChange={setAdAccounts}
											allLabel="All ad accounts"
											summaryNoun="ad accounts"
											headerNoun="Ad accounts"
										/>
										<LabeledMultiSelect
											options={campaignOptions}
											selected={campaigns}
											onChange={setCampaigns}
											allLabel="All campaigns"
											summaryNoun="campaigns"
											headerNoun="Campaigns"
										/>
									</div>
								</div>

								{/* Cadence + start. */}
								<div className="flex gap-4">
									<LabeledSelect
										label="Reporting interval"
										value={interval}
										options={INTERVAL}
										onChange={setInterval}
									/>
									<DatePickerField
										label="Starting date"
										value={startDate}
										onChange={setStartDate}
									/>
								</div>

								{/* Date range (half width per Figma). */}
								<div className="flex gap-4">
									<LabeledSelect
										label="Date range"
										info="Defines the data period included each time the report is generated."
										value={range}
										options={RANGE}
										onChange={setRange}
									/>
									<div className="w-full" aria-hidden />
								</div>

								{/* Email recipients — one row per address the report is sent to. Add with
				    Enter or the Invite button; remove inline with the trash. */}
								<div className="flex flex-col gap-1.5">
									<FieldLabel>Email recipients</FieldLabel>
									{recipients.length > 0 && (
										<div className="flex flex-col gap-1.5">
											<AnimatePresence initial={false}>
												{recipients.map((email) => (
													// Animate the wrapper's height so add/remove eases instead of popping.
													// overflow-hidden is required: the h-10 row inside is the 'auto' target,
													// and clipping keeps the collapsing row from spilling past the wrapper.
													<motion.div
														key={email}
														className="overflow-hidden"
														initial={{ height: 0, opacity: 0 }}
														animate={{ height: 'auto', opacity: 1 }}
														exit={{ height: 0, opacity: 0 }}
														transition={
															reduced
																? { duration: 0 }
																: { duration: 0.2, ease: [0.16, 1, 0.3, 1] }
														}
													>
														<div
															// h-10 (not min-h) so the chip is EXACTLY the input's height — its
															// h-7 trash button otherwise pushed a min-h-[40px] box to ~46px.
															// pr-1.5 (6px) matches the trash's top/bottom inset (h-7 in an h-10
															// row = 6px); left keeps 12px so the email/icon isn't cramped.
															className="flex h-10 items-center justify-between gap-2 rounded-lg border border-[rgb(var(--cg-border))] bg-white pl-3 pr-1.5"
														>
															<span className="flex min-w-0 items-center gap-2 text-base text-gray-900">
																<Mail01 className="h-4 w-4 shrink-0 text-[rgb(var(--cg-text-tertiary))]" />
																<span className="truncate">{email}</span>
															</span>
															<button
																type="button"
																onClick={() => removeRecipient(email)}
																aria-label={`Remove ${email}`}
																className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[rgb(var(--cg-text-tertiary))] transition-colors hover:bg-[rgb(var(--cg-canvas))] hover:text-[rgb(var(--cg-text-secondary))]"
															>
																<Trash01 className="h-4 w-4" />
															</button>
														</div>
													</motion.div>
												))}
											</AnimatePresence>
										</div>
									)}
									<div className="flex items-stretch gap-2">
										<input
											type="email"
											value={recipientDraft}
											onChange={(e) => {
												setRecipientDraft(e.target.value)
												if (recipientError) setRecipientError(null)
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault()
													addRecipient()
												}
											}}
											placeholder="teammate@company.com"
											// pa-recipient-input: the focus border/ring is set in CSS — the
											// global @tailwindcss/forms `[type=email]:focus` rule out-specifies a
											// `focus:border-*` utility and forces a blue border + 2px ring, so the
											// brand-purple border has to be reasserted with !important there.
											className="pa-recipient-input min-h-[40px] w-full rounded-lg border border-[rgb(var(--cg-border))] bg-white px-3 py-2 text-base text-gray-900 shadow-[0px_1px_1px_rgba(16,24,40,0.05)] outline-none placeholder:text-[#6b7280]"
										/>
										<AppButton
											variant="primary"
											size="sm"
											className="!min-h-[40px] shrink-0"
											label="Add"
											leftIcon={<Plus className="h-4 w-4" />}
											onClick={addRecipient}
										/>
									</div>
									{recipientError && (
										<span className="text-xs text-[#b8413a]">
											{recipientError}
										</span>
									)}
								</div>
							</>
						) : (
							<>
								{/* White-label branding: the toggle leads; turning it off ships the
				    report unbranded. A logo is required once branding is on. */}
								<div className="flex flex-col gap-3">
									<div className="flex items-center gap-1.5">
										{/* Unique id so the DS Toggle's label htmlFor doesn't collide
										    with the per-slot toggles elsewhere (shared `cg-toggle--`). */}
										<Toggle
											id="wl-report-toggle"
											size="md"
											checked={whiteLabel}
											onChange={onToggleWhiteLabel}
											label="White label report"
										/>
										<Tooltip
											content="Add your brand, agency, or client logo to the report header."
											position="top"
											portal
											className="pa-drawer-tip"
										>
											<span
												className="flex h-3.5 w-3.5 cursor-help items-center justify-center text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-3.5 [&>svg]:w-3.5"
												aria-label="What is a white label report?"
											>
												<HelpCircle />
											</span>
										</Tooltip>
									</div>
									{/* Branding fields are always shown regardless of the toggle
									    (Esteban): the toggle only flips whether the report ships
									    branded — it no longer hides logo / colour / preview. */}
									<div className="flex flex-col gap-4 pt-0.5">
										{/* Logo source — the workspace or website image, or a custom upload. The
										    workspace / website options disable until that image is set. */}
										<div className="flex flex-col gap-1.5">
											<FieldLabel>Logo</FieldLabel>
											<RadioGroup
												aria-label="Logo source"
												value={logoSource}
												onValueChange={(v) =>
													setLogoSource(v as WhiteLabelLogoSource)
												}
											>
												{LOGO_SOURCES.map(({ value, label, emptyTip }) => {
													// A workspace/website source with no image yet is disabled;
													// its tooltip says why.
													const disabled = !!emptyTip && !logoImages[value]
													return (
														<div key={value} className="flex flex-col">
															{/* w-fit keeps the trigger from stretching to the full column
															    width (the flex-col's default align-items: stretch), so the
															    tooltip anchors to the radio itself, not a full-width box. */}
															<div className="w-fit">
																{disabled ? (
																	<Tooltip
																			content={
																			value === 'website' && websiteUsesFavicon
																				? 'This website is using the default favicon, which cannot be used in exported reports. Go to Settings > Website > Preferences and replace it with a logo image.'
																				: emptyTip
																		}
																		position="top"
																		portal
																		className="pa-drawer-tip pa-gate-tip translate-x-[18px]"
																	>
																		<span className="inline-flex w-fit">
																			<Radio value={value} label={label} disabled />
																		</span>
																	</Tooltip>
																) : (
																	<Radio value={value} label={label} />
																)}
															</div>
														{/* The image + replace card slides in directly below whichever
														    source is selected. The height clip is on the outer element with
														    the spacing inside it (so no gap pops in first); the inner element
														    slides + fades so it reads as easing into place. */}
														<AnimatePresence initial={false}>
															{logoSource === value && (
																<motion.div
																	key={`wl-card-${value}`}
																	initial={{ height: 0 }}
																	animate={{ height: 'auto' }}
																	exit={{ height: 0 }}
																	transition={
																		reduced
																			? { duration: 0 }
																			: { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
																	}
																	style={{ overflow: 'hidden' }}
																>
																	<motion.div
																		className="pt-2"
																		initial={{ opacity: 0, y: -8 }}
																		animate={{ opacity: 1, y: 0 }}
																		exit={{ opacity: 0, y: -8 }}
																		transition={
																			reduced
																				? { duration: 0 }
																				: { duration: 0.28, ease: [0.22, 1, 0.36, 1] }
																		}
																	>
																		<LogoCard
																			image={effectiveLogo}
																			onReplace={() => fileRef.current?.click()}
																		/>
																	</motion.div>
																</motion.div>
															)}
														</AnimatePresence>
													</div>
												)
												})}
											</RadioGroup>
											{/* Hidden file input driven by every card's Replace button. */}
											<input
												ref={fileRef}
												type="file"
												accept="image/*"
												className="hidden"
												onChange={onPickLogo}
											/>
											{brandingNeedsLogo && (
												<span className="mt-1 block text-xs text-[#b8413a]">
													Add a logo to save.
												</span>
											)}
										</div>

										{/* Brand colour — drives the tint of the report preview below. */}
										<ColorPickerField
											label="Brand color"
											value={brandColor}
											onChange={setBrandColor}
										/>

										{/* Report header preview — a mock of the branded report header so the logo +
							    brand colour read together the way they'll appear on the output. */}
										<div className="flex flex-col gap-1.5">
											<FieldLabel>Report header preview</FieldLabel>
											<div className="overflow-hidden rounded-xl border border-[rgb(var(--cg-border))]">
												<div
													className="flex items-center gap-3 p-4"
													style={{ backgroundColor: brandColor }}
												>
													<span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white p-1.5">
														{effectiveLogo ? (
															<LogoImg
																key={effectiveLogo}
																src={effectiveLogo}
																className="h-full w-full object-contain"
															/>
														) : (
															<Image01 className="h-5 w-5 text-[rgb(var(--cg-text-tertiary))]" />
														)}
													</span>
													<div className="flex min-w-0 flex-col">
														<span
															className="truncate text-sm font-semibold"
															style={{ color: readableOn(brandColor) }}
														>
															{name}
														</span>
														<span
															className="truncate text-xs"
															style={{
																color: readableOn(brandColor),
																opacity: 0.8
															}}
														>
															Protection analytics report
														</span>
													</div>
												</div>
												<div className="flex flex-col gap-2 bg-white p-4">
													<div className="h-2 w-2/3 rounded-full bg-[rgb(var(--cg-surface-2))]" />
													<div className="h-2 w-1/2 rounded-full bg-[rgb(var(--cg-surface-subtle))]" />
												</div>
											</div>
										</div>
									</div>
								</div>
							</>
						)}
					</motion.div>
				</AnimatePresence>
			</div>

			{/* Footer — per tab. Schedule gets the schedule CTAs; White label gets its
			    own Save that commits the branding profile. Download is a persistent
			    top-of-drawer action; branding follows the workspace's plan access.
			    Uniform 16px padding (p-4) around the buttons, not the body's 24px
			    gutter — Esteban (Cherry #0009): "12–16px padding all around". */}
			{tab === 'schedule' &&
				(schedule ? (
					// Keep the status full-width, with navigation and actions below it.
					<div className="flex flex-col gap-3 border-t border-[rgb(var(--cg-border))] p-4">
						<div className="flex items-center justify-between gap-3">
							<p className="text-sm text-[#6b7280]">
								Scheduled {schedule.interval.toLowerCase()} · next report{' '}
								{formatDisplay(
									nextGeneration(schedule.interval, schedule.startDate)
								)}
								{schedule.recipients.length > 0 &&
									` · ${schedule.recipients.length} recipient${
										schedule.recipients.length === 1 ? '' : 's'
									}`}
							</p>
						</div>
						<div className="flex flex-wrap items-center justify-between gap-2">
							{pastReportsLink ?? <span />}
							<div className="ml-auto flex items-center gap-2">
								<AppButton
									variant="destructive"
									size="sm"
									label="Stop scheduling"
									onClick={() => setConfirmingStop(true)}
								/>
								<AppButton
									variant="primary"
									size="sm"
									label="Save changes"
									disabled={!dirty}
									onClick={handleSchedule}
								/>
							</div>
						</div>
					</div>
				) : (
					<div className="flex items-center justify-between gap-3 border-t border-[rgb(var(--cg-border))] p-4">
						{pastReportsLink ?? <span />}
						<AppButton
							variant="primary"
							size="md"
							label="Schedule"
							onClick={handleSchedule}
						/>
					</div>
				))}

				{/* White-label footer — Save commits the working branding to the store.
				    Enabled only when the working copy differs from the saved profile and
				    is valid (an enabled profile needs a logo); the tooltip explains the
				    logo requirement on the disabled Save. */}
				{tab === 'whitelabel' && (
					<div className="flex items-center justify-end gap-3 border-t border-[rgb(var(--cg-border))] p-4">
						{brandingNeedsLogo ? (
							<Tooltip
								content={WHITE_LABEL_SAVE_TIP}
								position="top"
								portal
								className="pa-drawer-tip pa-gate-tip"
							>
								<span className="inline-flex [&>button]:pointer-events-none">
									<AppButton
										variant="primary"
										size="md"
										label="Save"
										disabled
										onClick={onSaveWhiteLabel}
									/>
								</span>
							</Tooltip>
						) : (
							<AppButton
								variant="primary"
								size="md"
								label="Save"
								disabled={!canSaveWhiteLabel}
								onClick={onSaveWhiteLabel}
							/>
						)}
					</div>
				)}

			{/* Stopping is destructive, so it confirms through the DS ConfirmDialog
			    rather than an inline footer swap.
			    Portaled to <body> for TWO reasons, both load-bearing:
			      1. the drawer PANEL carries a framer transform, and a transformed
			         ancestor becomes the containing block for `position: fixed` — left
			         inside, the Modal's `fixed inset-0` resolves to the 460px panel, so
			         the dialog centres over the drawer and its scrim dims only the
			         drawer instead of the page;
			      2. the DS Modal is `z-50`, which loses to the drawer's z-[9999] once
			         they're siblings under <body> — hence the z-[10000] wrapper, whose
			         `relative` + z-index makes a stacking context that outranks the
			         drawer WITHOUT re-introducing a containing block (only transform /
			         filter / perspective do that, not `relative`).
			    Same portal-to-body convention as canvas-header's unsaved-changes guard. */}
			{createPortal(
				<div className="relative z-[10000]">
					<ConfirmDialog
						open={confirmingStop}
						onClose={() => setConfirmingStop(false)}
						onConfirm={handleStopSchedule}
						tone="danger"
						title="Stop scheduling?"
						description={`“${name}” will stop being sent. You can schedule it again anytime.`}
						confirmLabel="Stop scheduling"
						cancelLabel="Keep schedule"
					/>
				</div>,
				document.body
			)}
		</>
	)
}

// ── Drawer ───────────────────────────────────────────────────────────────────
// "View scheduled reports" — report history lives on the dedicated Scheduled Reports
// page, not a buried tab (Cherry #0006). It sits in the Schedule footer's empty
// left slot rather than the header (Cherry #0010: up there it added a mostly
// empty row and made the header taller than it needs to be).
const PastReportsLink = ({ onClick }: { onClick: () => void }) => (
	<button
		type="button"
		onClick={onClick}
		className="inline-flex items-center gap-1 text-sm font-medium text-[rgb(var(--cg-brand))] transition-colors hover:text-[#443ac9] [&>svg]:h-4 [&>svg]:w-4"
	>
		View scheduled reports
		<ArrowUpRight />
	</button>
)

type ShareReportDrawerProps = {
	open: boolean
	onClose: () => void
	reportName?: string
}

export const ShareReportDrawer = (props: ShareReportDrawerProps) => {
	const { accountId, assetId } = useParams()
	return <WebsiteShareReportDrawer key={`${accountId}/${assetId}`} {...props} />
}

const WebsiteShareReportDrawer = ({
	open,
	onClose,
	reportName = 'Keyword report'
}: ShareReportDrawerProps) => {
	const { accountId, assetId } = useParams()
	const branding = useWebsiteBranding(accountId, assetId, open)
	const [render, setRender] = useState(open)
	const [shown, setShown] = useState(false)
	// Active tab; reset to Schedule whenever the drawer (re)opens.
	const [tab, setTab] = useState<ShareTab>('schedule')
	const canWhiteLabel = useAccountStore((s) => s.hasFeature('reports_white_label'))
	useEffect(() => {
		if (!canWhiteLabel) setTab('schedule')
	}, [canWhiteLabel])

	// Report name — lifted from ShareForm (same lift as the branding fields below)
	// so onDownload can title the PDF with what recipients see. Seeded from the
	// schedule on record, else the active view label.
	const activeViewId = useReportLayoutStore((s) => s.activeViewId)
	// The schedule on record for the active view — the backend's row (created by
	// anyone in the workspace, from any browser), never a local cache.
	const {
		schedule,
		isLoading: scheduleLoading,
		refresh: refreshSchedule
	} = useViewSchedule(activeViewId)
	const scheduleName = schedule?.name
	const [name, setName] = useState(scheduleName ?? reportName)
	// useState only captures the first value, so switching the active view (or an
	// external rename) wouldn't move the field. Re-seed the report name whenever the
	// view / its schedule name changes. Typing edits `name` locally and doesn't touch
	// scheduleName/reportName, so this never clobbers what the user is typing.
	useEffect(() => {
		setName(scheduleName ?? reportName)
	}, [activeViewId, scheduleName, reportName])

	// The server's website profile is committed; the fields below are a draft.
	const savedBranding = branding.saved
	const workspaceImage = useAccountStore((s) =>
		s.account?.id === accountId ? s.account?.avatar ?? null : null
	)
	const websiteImage = branding.asset?.avatar ?? null
	const websiteUsesFavicon = useWebsiteFavicon(
		open ? branding.asset?.name : undefined,
		websiteImage
	)
	const resolveLogo = (
		source: WhiteLabelLogoSource,
		custom: string | null
	): string | null =>
		source === 'workspace'
			? workspaceImage
			: source === 'website'
				? websiteImage
				: custom

	const [whiteLabel, setWhiteLabel] = useState(savedBranding.enabled)
	const [logoSource, setLogoSource] = useState<WhiteLabelLogoSource>(
		savedBranding.logoSource
	)
	const [customLogo, setCustomLogo] = useState<string | null>(
		savedBranding.customLogo
	)
	// The raw picked File (kept only until Save), so we can upload it to the
	// white-label logo endpoint — scheduled reports render server-side and need a
	// real URL, not the client-only data URI. Null once there's nothing new to send.
	const [customLogoFile, setCustomLogoFile] = useState<File | null>(null)
	const [brandColor, setBrandColor] = useState(savedBranding.brandColor)
	const logoReader = useRef<FileReader | null>(null)
	useEffect(() => () => {
		if (logoReader.current?.readyState === FileReader.LOADING) logoReader.current.abort()
	}, [open])
	useEffect(() => {
		setWhiteLabel(savedBranding.enabled)
		setLogoSource(savedBranding.logoSource)
		setCustomLogo(savedBranding.customLogo)
		setBrandColor(savedBranding.brandColor)
		setCustomLogoFile(null)
	}, [savedBranding])
	// Ad account / campaign scope — lifted from ShareForm (same lift as the
	// white-label fields above) so onDownload can read the current selection.
	// Options are the website's REAL ad accounts / campaigns from the store.
	const scopeOptions = useScopeOptions()
	const [adAccounts, setAdAccounts] = useState<string[]>([])
	const [campaigns, setCampaigns] = useState<string[]>([])
	// Default the scope to everything once the real options load (= "All …"), then
	// leave it under the user's control.
	const scopeInited = useRef(false)
	useEffect(() => {
		if (scopeInited.current) return
		if (!scopeOptions.adAccounts.length && !scopeOptions.campaigns.length) return
		scopeInited.current = true
		setAdAccounts(scopeOptions.adAccounts.map((a) => a.id))
		setCampaigns(scopeOptions.campaigns.map((c) => c.id))
	}, [scopeOptions])

	// Data URIs are only draft previews; saved branding uses the server upload URL.
	const onPickLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0]
		if (!f) return
		if (logoReader.current?.readyState === FileReader.LOADING) logoReader.current.abort()
		const r = new FileReader()
		logoReader.current = r
		r.onload = () => {
			// A picked image is a custom logo — switch the source so it becomes the
			// effective logo and its card follows, whichever source was selected.
			setCustomLogo(r.result as string)
			setCustomLogoFile(f)
			setLogoSource('custom')
			// Uploading a logo IS the white-label intent — flip the master toggle on
			// so Save can't silently commit a disabled profile. (Esteban got bitten:
			// logo uploaded + saved + toast, but the report still shipped
			// ClickGuard-branded because the toggle was off.)
			setWhiteLabel(true)
		}
		r.readAsDataURL(f)
		e.target.value = ''
	}

	// Toggling white-label only flips whether the working profile is branded — the
	// branding fields stay visible either way.
	const onToggleWhiteLabel = (e: React.ChangeEvent<HTMLInputElement>) =>
		setWhiteLabel(e.target.checked)

	// The working profile's resolved logo (whatever the selected source yields).
	const effectiveLogo = resolveLogo(logoSource, customLogo)
	// Each source's image; a workspace/website source with none is shown disabled.
	const logoImages: Record<WhiteLabelLogoSource, string | null> = {
		workspace: workspaceImage,
		website: websiteImage,
		custom: customLogo
	}

	// White-label Save state. The profile is savable once it differs from what's
	// committed AND is valid — an enabled profile must resolve to a logo. Download
	// and scheduling remain available; branding also requires plan access.
	const brandingNeedsLogo = whiteLabel && !effectiveLogo
	const brandingDirty =
		whiteLabel !== savedBranding.enabled ||
		logoSource !== savedBranding.logoSource ||
		customLogo !== savedBranding.customLogo ||
		brandColor !== savedBranding.brandColor
	const canSaveWhiteLabel =
		!!accountId && !!assetId && canWhiteLabel && branding.ready &&
		!branding.saving && brandingDirty && !brandingNeedsLogo
	const onSaveWhiteLabel = async () => {
		if (!canSaveWhiteLabel) return
		try {
			const saved = await branding.save(
				{ enabled: whiteLabel, logoSource, customLogo, brandColor },
				customLogoFile
			)
			if (saved) {
				toast.success('White label settings saved.')
			}
		} catch {
			toast.error('Could not save white label settings. Please try again.')
		}
	}

	const trackReportEvent = useTrackPostHogEvent()
	const [downloading, setDownloading] = useState(false)
	// The committed profile's logo, in a form the PDF builder can bake in. The
	// saved images are storage URLs. Fetch their bytes through the authenticated
	// proxy so PDFs work in every browser without storage-bucket CORS access.
	const resolveDownloadLogo = async (): Promise<string | Blob | null> => {
		const { enabled, logoSource, customLogo } = savedBranding
		if (!canWhiteLabel || !enabled) return null
		const local = resolveLogo(logoSource, customLogo)
		if (!accountId || !assetId) return local
		try {
			return await ScheduledReports.whiteLabelLogoBlob(
				accountId,
				assetId,
				logoSource === 'custom' ? 'upload' : logoSource
			)
		} catch {
			return local
		}
	}
	const onDownload = async () => {
		if (downloading || (canWhiteLabel && (!branding.ready || branding.saving))) return
		setDownloading(true)
		try {
			// Lazy import keeps react-pdf + capture out of the page bundle.
			const { downloadReport } = await import('./pdf/download-report')
			// Downloads apply the COMMITTED branding profile, not the drawer's
			// unsaved working copy, so the output always matches what was saved.
			// The orchestrator must not import this module (it lives outside the
			// react-pdf lazy boundary), so the drawer — the owner of AD_ACCOUNTS /
			// CAMPAIGNS — passes the full option lists alongside the selection.
			await downloadReport(
				{
					whiteLabel: canWhiteLabel && savedBranding.enabled,
					logo: await resolveDownloadLogo(),
					brandColor:
						canWhiteLabel && savedBranding.enabled
							? savedBranding.brandColor
							: DEFAULT_BRAND_COLOR
				},
				{
					accounts: scopeOptions.adAccounts.map(({ id, name }) => ({ id, name })),
					selectedAccountIds: adAccounts,
					campaigns: scopeOptions.campaigns.map(({ id, name }) => ({ id, name })),
					selectedCampaignIds: campaigns
				},
				name
			)
			// Monitoring (§Monitoring): download event on the Share panel.
			trackReportEvent(PostHogEvent.DOWNLOAD, PostHogPath.ANALYTICS_REPORTS, null)
			// Adoption counter: the PDF is built client-side, so ping the backend to
			// record the download (best-effort).
			if (accountId && assetId) {
				AnalyticsReport.trackDownload(accountId, assetId)
			}
		} catch {
			toast.error('Could not build the PDF report')
		} finally {
			setDownloading(false)
		}
	}

	// The report history used to live in a "Past reports" tab here, which was hard
	// to find (Cherry #0006). The drawer is now single-purpose — schedule + branding
	// — and links out to the dedicated Scheduled Reports page instead. In the auth-free
	// preview there's no account/asset in the URL, so the target is unreachable —
	// hide the link entirely there rather than render a dead-end affordance.
	const navigate = useNavigate()
	const canViewReports = !!(accountId && assetId)
	const goToReports = () => {
		if (!canViewReports) return
		onClose()
		navigate(`/workspace/${accountId}/asset/${assetId}/reports`)
	}

	// Mount → next frame slide in; close → slide out, then unmount.
	useEffect(() => {
		if (open) {
			setRender(true)
			setTab('schedule')
			const raf = requestAnimationFrame(() => setShown(true))
			return () => cancelAnimationFrame(raf)
		}
		setShown(false)
		const t = setTimeout(() => setRender(false), 300)
		return () => clearTimeout(t)
	}, [open])

	// Esc closes while open.
	useEffect(() => {
		if (!open) return
		const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
		document.addEventListener('keydown', onEsc)
		return () => document.removeEventListener('keydown', onEsc)
	}, [open, onClose])

	// Focus management for the aria-modal dialog: move focus into the drawer once
	// it's mounted + shown, trap Tab within it, and restore focus to the trigger on
	// close. Keyed on `shown` (not `open`) so the portal — and dialogRef — exists;
	// keying on `open` fired a frame before render mounted, so focus no-op'd.
	const dialogRef = useRef<HTMLDivElement>(null)
	const restoreFocusRef = useRef<HTMLElement | null>(null)
	useEffect(() => {
		if (!shown) return
		const root = dialogRef.current
		if (!root) return
		restoreFocusRef.current = document.activeElement as HTMLElement | null
		const focusables = () =>
			[
				...root.querySelectorAll<HTMLElement>(
					'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
				)
			].filter(
				(el) =>
					el.getBoundingClientRect().width > 0 &&
					el.getBoundingClientRect().height > 0
			)
		// Move focus in — the first focusable (Close) so Shift+Tab from it wraps to
		// the last, rather than the root (which would leak Shift+Tab to the page).
		;(focusables()[0] ?? root).focus()
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Tab') return
			const f = focusables()
			if (!f.length) {
				e.preventDefault()
				root.focus()
				return
			}
			const first = f[0]
			const last = f[f.length - 1]
			const active = document.activeElement
			if (e.shiftKey && (active === first || !root.contains(active))) {
				e.preventDefault()
				last.focus()
			} else if (!e.shiftKey && (active === last || !root.contains(active))) {
				e.preventDefault()
				first.focus()
			}
		}
		document.addEventListener('keydown', onKey)
		return () => {
			document.removeEventListener('keydown', onKey)
			restoreFocusRef.current?.focus?.()
		}
	}, [shown])

	// Drop pinned third-party chat widgets behind the drawer while it's open
	// (they pin to the max z-index, so nothing can sit above them otherwise).
	useEffect(() => {
		if (!open) return
		const SUPPRESS = [
			'#hubspot-messages-iframe-container',
			'#hubspot-conversations-iframe',
			'#ug-product-updates-frame',
			'.widget-align-right'
		]
		// el -> original inline z-index, so close restores it exactly.
		const saved = new Map<HTMLElement, string>()
		const suppress = () => {
			SUPPRESS.forEach((sel) =>
				document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
					if (el.style.zIndex === '0') return
					if (!saved.has(el)) saved.set(el, el.style.zIndex)
					el.style.zIndex = '0'
				})
			)
		}
		suppress()
		// Chat widgets (HubSpot) inject asynchronously — and when the drawer
		// auto-opens on mount (the Manage deep-link) they routinely land AFTER this
		// first pass, so a one-shot suppress misses them and they float over the
		// drawer. Keep suppressing new/late nodes while the drawer is open. Our own
		// z-index writes are idempotent (the `=== '0'` guard) so the observer that
		// they trigger can't loop.
		const mo = new MutationObserver(suppress)
		mo.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['style', 'class']
		})
		return () => {
			mo.disconnect()
			saved.forEach((z, el) => (el.style.zIndex = z))
		}
	}, [open])

	if (!render) return null

	return createPortal(
		<div
			ref={dialogRef}
			tabIndex={-1}
			className="fixed inset-0 z-[9999] outline-none"
			role="dialog"
			aria-modal="true"
			aria-label="Share report"
		>
			{/* Scrim. */}
			<div
				className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
					shown ? 'opacity-100' : 'opacity-0'
				}`}
				onClick={onClose}
			/>
			{/* Panel — floats with a 12px gutter on top/right/bottom (rounded card, not
			    edge-to-edge), matching the chart drawer shell. overflow-hidden clips the
			    scrolling body to the radius; the closed state slides out 100% + the 12px
			    gap so no sliver peeks at the edge. */}
			<div
				className={`absolute bottom-2 right-2 top-2 flex w-[460px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-[rgb(var(--cg-border))] bg-white shadow-[0px_8px_24px_-4px_rgba(16,24,40,0.18)] transition-transform duration-300 ease-out ${
					shown ? 'translate-x-0' : 'translate-x-[calc(100%+8px)]'
				}`}
			>
				{/* Header. Single-purpose now (schedule + branding); the bottom border gives
				    the scrolling body below a clean cut-off (matches the customize drawer). */}
				<div className="flex flex-col gap-2 border-b border-[rgb(var(--cg-border))] px-6 pb-4 pt-6">
					<div className="flex items-center justify-between gap-4">
						<h2 className="text-lg font-medium text-[rgb(var(--cg-brand-dark))]">Share report</h2>
						<button
							type="button"
							aria-label="Close"
							onClick={onClose}
							className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[rgb(var(--cg-text-tertiary))] transition-colors hover:bg-black/5 hover:text-gray-700 [&>svg]:h-4 [&>svg]:w-4"
						>
							<XClose />
						</button>
					</div>
					<div className="flex flex-wrap items-center justify-between gap-3">
						<SegmentedTabs
							value={tab}
							onChange={(v) => {
								if (v !== 'whitelabel' || canWhiteLabel) setTab(v as ShareTab)
							}}
						>
							<SegmentedTabList>
								<SegmentedTab value="schedule" label="Schedule" />
								{canWhiteLabel ? (
									<SegmentedTab value="whitelabel" label="White label" />
								) : (
									<Tooltip
										content="Available on Pro and Custom plans; upgrade to whitelabel your exported reports."
										position="bottom"
										portal
										className="pa-drawer-tip pa-gate-tip"
									>
										<span tabIndex={0} className="inline-flex [&>button]:pointer-events-none">
											<SegmentedTab
												value="whitelabel"
												label="White label"
												disabled
												counter={
													<span className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-brand-tint-text">
														PRO
													</span>
												}
											/>
										</span>
									</Tooltip>
								)}
							</SegmentedTabList>
						</SegmentedTabs>
						<AppButton
							variant="secondary"
							size="sm"
							className="shrink-0"
							label={downloading ? 'Preparing…' : 'Download report'}
							leftIcon={<Download01 className="h-4 w-4" />}
							isLoading={downloading}
							disabled={canWhiteLabel && (!branding.ready || branding.saving)}
							loader="dots"
							onClick={onDownload}
						/>
					</div>
				</div>

				{/* Body. Until the schedule on record is known, hold the form back —
				    seeding it with defaults and then swapping to the real cadence would
				    flash the wrong footer. Keyed by the schedule id so a create / stop
				    remounts it seeded from what the backend now holds. */}
				{branding.error && canWhiteLabel ? (
					<div role="alert" className="p-6">
						Could not load white label settings. Close and reopen this panel to retry.
					</div>
				) : scheduleLoading || (canWhiteLabel && !branding.ready) ? (
					<div className="flex flex-1 items-center justify-center">
						<DotMatrix size="md" label="Loading report settings" />
					</div>
				) : (
				<ShareForm
					key={schedule?.reportId ?? 'unscheduled'}
					schedule={schedule}
					onScheduleChanged={refreshSchedule}
					name={name}
					setName={setName}
					onSchedule={onClose}
					tab={tab}
					whiteLabel={whiteLabel}
					logoSource={logoSource}
						setLogoSource={setLogoSource}
						effectiveLogo={effectiveLogo}
						logoImages={logoImages}
					websiteUsesFavicon={websiteUsesFavicon}
					brandColor={brandColor}
					adAccounts={adAccounts}
					campaigns={campaigns}
					adAccountOptions={scopeOptions.adAccounts}
					campaignOptions={scopeOptions.campaigns}
					onToggleWhiteLabel={onToggleWhiteLabel}
					onPickLogo={onPickLogo}
					setBrandColor={setBrandColor}
					setAdAccounts={setAdAccounts}
					setCampaigns={setCampaigns}
					brandingNeedsLogo={brandingNeedsLogo}
					canSaveWhiteLabel={canSaveWhiteLabel}
					onSaveWhiteLabel={onSaveWhiteLabel}
					// Only when the Scheduled Reports page is reachable (real workspace
					// route) — omitted in the auth-free preview.
					pastReportsLink={canViewReports ? <PastReportsLink onClick={goToReports} /> : null}
				/>
				)}
			</div>
			{/* Curtain over the whole page while the PDF is built — the chart capture
			    reshapes the live panels and flips the theme, none of which the user
			    should watch. Portaled to <body> above the drawer. */}
			{downloading && <DownloadOverlay />}
		</div>,
		document.body
	)
}
